import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdRefresh } from 'react-icons/md';
import attendanceAPI from '../../api/attendanceAPI';
import { useAttendanceAuth } from '../../context/AttendanceAuthContext';
import {
  canCancelBooking,
  formatBookingDay,
  formatBookingTime,
  getCameraStateStyle,
} from '../../utils/cameraBookingUtils';

const STATE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * Faculty admin's camera requests, filterable by state, with cancel.
 */
function MyCameraRequests() {
  const navigate = useNavigate();
  const { getToken } = useAttendanceAuth();

  const [stateFilter, setStateFilter] = useState('all');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = stateFilter === 'all' ? {} : { state: stateFilter };
        const result = await attendanceAPI.listCameraBookings(params, getToken());
        if (!cancelled) setBookings(result.bookings || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load your requests.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter, reloadKey]);

  const handleCancel = async (booking) => {
    if (!window.confirm(`Cancel the camera request "${booking.purpose}"?`)) return;
    setBusyId(booking.id);
    setError('');
    try {
      await attendanceAPI.cancelCameraBooking(booking.id, getToken());
      setReloadKey((key) => key + 1);
    } catch (err) {
      setError(err.message || 'Could not cancel this request.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">My Camera Requests</h2>
          <p className="text-sm text-gray-500">Track your requests and cancel any that are no longer needed.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setReloadKey((key) => key + 1)}
            className="flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
          >
            <MdRefresh /> Refresh
          </button>
          <button
            type="button"
            onClick={() => navigate('/attendance/camera/request')}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700"
          >
            <MdAdd /> New request
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATE_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStateFilter(filter.value)}
            className={`px-3 py-1.5 text-sm rounded-full border ${
              stateFilter === filter.value
                ? 'bg-green-600 border-green-600 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Reference</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Purpose</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">From</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">To</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Reject reason</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading…</td>
              </tr>
            )}
            {!loading && bookings.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-500">No requests found.</td>
              </tr>
            )}
            {!loading &&
              bookings.map((booking) => {
                const style = getCameraStateStyle(booking.state);
                return (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{booking.reference || '—'}</td>
                    <td className="px-4 py-3 text-gray-800">{booking.purpose}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {formatBookingDay(booking.start)} {formatBookingTime(booking.start)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {formatBookingDay(booking.end)} {formatBookingTime(booking.end)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded ${style.badge}`}>
                        {style.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-red-700">{booking.reject_reason || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {canCancelBooking(booking) ? (
                        <button
                          type="button"
                          onClick={() => handleCancel(booking)}
                          disabled={busyId === booking.id}
                          className="px-3 py-1.5 text-xs rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          {busyId === booking.id ? 'Cancelling…' : 'Cancel'}
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MyCameraRequests;
